import React, { useState, useCallback } from "react"
import dayjs from "dayjs"
import { ForFinancePayment } from "../types"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"

interface ForFinancePaymentItemProps {
  payment: ForFinancePayment
}

type CopyField =
  | "bankCode"
  | "branchCode"
  | "accountAddress"
  | "accountName"
  | "amount"
  | "remitterName"

interface CopyButtonProps {
  label: string
  value: string | number | null
  field: CopyField
  copiedField: CopyField | null
  onCopy: (field: CopyField, value: string) => void
}

const CopyButton: React.FC<CopyButtonProps> = ({ label, value, field, copiedField, onCopy }) => {
  const isCopied = copiedField === field
  const displayValue = value?.toString() ?? "-"
  const canCopy = value !== null && value !== undefined

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (canCopy) {
      onCopy(field, value.toString())
    }
  }

  return (
    <div className="flex flex-col">
      <span className="mb-1 text-lg font-medium text-gray-600">{label}</span>
      <div
        className={`flex items-center gap-1 ${
          canCopy ? "cursor-pointer rounded px-1 -mx-1 hover:bg-gray-100" : ""
        }`}
        onClick={handleClick}
        title={canCopy ? `${label}をコピー` : undefined}
      >
        <span className="text-black">{displayValue}</span>
        {canCopy &&
          (isCopied ? (
            <svg
              className="ml-1 h-4 w-4 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <svg
              className="ml-1 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          ))}
      </div>
    </div>
  )
}

const getAccountTypeLabel = (accountType: string | null | undefined): string => {
  if (!accountType) return "-"
  switch (accountType) {
    case "1":
      return "1(普通)"
    case "2":
      return "2(当座)"
    default:
      return accountType
  }
}

const ForFinancePaymentItem: React.FC<ForFinancePaymentItemProps> = ({ payment }) => {
  const [copiedField, setCopiedField] = useState<CopyField | null>(null)

  const formatAmount = (amount: number): string => {
    return amount.toLocaleString() + "円"
  }

  const url = `${SMOOTH_LINE_API_URL}/admin/for_finance/payments/${payment.id}/edit`

  const handleCopy = useCallback(async (field: CopyField, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error("コピーに失敗しました:", err)
    }
  }, [])

  const handleCardClick = () => {
    const newWindow = window.open(url, "_blank")
    if (newWindow) {
      newWindow.opener = null
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50">
      <div className="cursor-pointer p-4" onClick={handleCardClick}>
        <div className="flex gap-3 text-sm">
          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">ID</span>
            <span className="text-black">{payment.id}</span>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">Moving Application ID</span>
            <span className="text-black">{payment.applicationId}</span>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">立替金額</span>
            <span className="text-black">{formatAmount(payment.advancedRemittanceAmount)}</span>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">契約日</span>
            <span className="text-black">
              {payment.contractedAt ? dayjs(payment.contractedAt).format("YYYY/MM/DD") : ""}
            </span>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">ユーザー名</span>
            <span className="text-black">{payment.userName}</span>
          </div>

          <div className="flex flex-col md:col-span-2">
            <span className="mb-1 text-lg font-medium text-gray-600">加盟店名</span>
            <span className="text-black">{payment.storeName}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 px-4 py-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-lg font-medium text-gray-600">GANB入力用</span>
        </div>

        <div className="flex flex-wrap gap-4">
          <CopyButton
            label="銀行コード"
            value={payment.bankCode}
            field="bankCode"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyButton
            label="支店コード"
            value={payment.branchCode}
            field="branchCode"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <div className="flex flex-col">
            <span className="mb-1 text-lg font-medium text-gray-600">口座種別</span>
            <span className="text-black">{getAccountTypeLabel(payment.accountType)}</span>
          </div>
          <CopyButton
            label="口座番号"
            value={payment.accountAddress}
            field="accountAddress"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyButton
            label="口座名義"
            value={payment.accountName}
            field="accountName"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyButton
            label="振込金額"
            value={payment.advancedRemittanceAmount}
            field="amount"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyButton
            label="振込依頼人名"
            value={payment.remitterName}
            field="remitterName"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
        </div>
      </div>
    </div>
  )
}

export default ForFinancePaymentItem
